import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/ui/Icon';

// Minimal stub for generateClientEmail function
const generateClientEmail = async (extractedData, clientType = 'buyer') => {
  try {
    const clientName = extractedData.parties?.buyer || extractedData.parties?.seller || 'Client';
    const propertyAddress = extractedData.property?.address || 'Property address not specified';
    const dates = extractedData.dates || {};
    
    const datesList = Object.entries(dates)
      .map(([key, date]) => `• ${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: ${date.value || 'Not specified'}`)
      .join('\n') || '• No key dates extracted';

    const emailContent = `Subject: Contract Analysis Complete - ${propertyAddress}

Dear ${clientName},

Great news! Your contract analysis is complete. Here are the key details:

PROPERTY INFORMATION:
• Address: ${propertyAddress}
• Purchase Price: ${extractedData.property?.purchasePrice || 'Not specified'}

IMPORTANT DATES:
${datesList}

DEPOSITS & FINANCIAL:
• Deposits: ${extractedData.financial?.deposits || 'Not specified'}

${extractedData.nonStandardClauses?.length > 0 
  ? `SPECIAL ITEMS: ${extractedData.nonStandardClauses.length} items require your attention` 
  : 'GOOD NEWS: No special clauses found - standard contract terms apply'}

I'll be monitoring all deadlines and will reach out as each approaches. Please don't hesitate to call with any questions!

Best regards,
Your Real Estate Professional

---
Generated by AI Contract Analysis • Confidence: ${extractedData.confidence || 85}%`;

    return { success: true, content: emailContent };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

const EmailGenerationPanel = ({ contractData, onCopyEmail, onDownloadICS }) => {
  const [emailContent, setEmailContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [clientType, setClientType] = useState('buyer');
  const [hasGenerated, setHasGenerated] = useState(false);

  // 🔥 DEBUG: Log email component data
  console.log('🔥 EmailGenerationPanel - contractData:', JSON.stringify(contractData, null, 2));
  console.log('🔥 EmailGenerationPanel - extractedData:', contractData?.extractedData);

  // Generate email when component mounts if we have real contract data
  useEffect(() => {
    if (contractData?.extractedData && !hasGenerated) {
      generateEmail();
    } else if (!contractData?.extractedData) {
      // Show demo email if no real data
      setEmailContent(`
Subject: Contract Analysis Summary - Demo

Dear Valued Client,

This is a demonstration of our AI-powered contract analysis system. To see a real email summary:

1. Upload an actual contract document
2. Let our AI analyze the content
3. Receive a personalized summary with key dates and terms

Our system can extract:
• Property details and purchase price
• Important deadlines and dates
• Escrow and financing information
• Special clauses requiring attention

Ready to try it with your real contract?

Best regards,
Your Real Estate Professional

---
Generated by ContractParser Pro AI
      `);
    }
  }, [contractData, hasGenerated]);

  const generateEmail = async () => {
    if (!contractData?.extractedData) return;

    setIsGenerating(true);
    try {
      const result = await generateClientEmail(contractData.extractedData, clientType);
      
      if (result.success) {
        setEmailContent(result.content);
        setHasGenerated(true);
      } else {
        setEmailContent(`
Subject: Contract Analysis Summary

Dear Client,

We've completed the analysis of your real estate contract. Here's a summary of the key information:

Property: ${contractData.extractedData.property?.address || 'Address not specified'}
Purchase Price: ${contractData.extractedData.property?.purchasePrice || 'Price not specified'}

Key Dates:
${contractData.extractedData.dates ? Object.entries(contractData.extractedData.dates)
  .map(([key, date]) => `• ${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: ${date.value}`)
  .join('\n') : '• No key dates extracted'}

${contractData.extractedData.nonStandardClauses?.length > 0 
  ? `Special Items: ${contractData.extractedData.nonStandardClauses.length} items require attention`
  : 'No special clauses found - standard contract terms apply'}

Please review these details and let me know if you have any questions.

Best regards,
Your Real Estate Agent

Error: ${result.error}
        `);
      }
    } catch (error) {
      console.error('Error generating email:', error);
      setEmailContent('Error generating email. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(emailContent);
      if (onCopyEmail) onCopyEmail();
    } catch (error) {
      console.error('Failed to copy email:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = emailContent;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      if (onCopyEmail) onCopyEmail();
    }
  };

  const handleRegenerateEmail = () => {
    setHasGenerated(false);
    generateEmail();
  };

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="Mail" size={20} color="var(--color-primary)" />
          <h4 className="font-medium text-foreground">Client Summary Email</h4>
          {contractData?.extractedData && (
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
              AI Generated
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {contractData?.extractedData && (
            <select
              value={clientType}
              onChange={(e) => {
                setClientType(e.target.value);
                setHasGenerated(false);
              }}
              className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
            >
              <option value="buyer">Buyer Client</option>
              <option value="seller">Seller Client</option>
            </select>
          )}
        </div>
      </div>

      {/* Email content area */}
      <div className="flex-1 bg-muted/30 rounded-lg border border-border overflow-hidden">
        {isGenerating ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin mb-2">
                <Icon name="Loader2" size={20} color="var(--color-primary)" />
              </div>
              <p className="text-sm text-muted-foreground">Generating personalized email...</p>
            </div>
          </div>
        ) : (
          <div className="h-full p-4 overflow-auto">
            <pre className="whitespace-pre-wrap text-sm text-foreground font-body leading-relaxed">
              {emailContent || 'No email content generated yet.'}
            </pre>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between space-x-3">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Copy"
            iconPosition="left"
            onClick={handleCopyToClipboard}
            disabled={!emailContent || isGenerating}
          >
            Copy Email
          </Button>

          {contractData?.extractedData && (
            <Button
              variant="outline" 
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={handleRegenerateEmail}
              disabled={isGenerating}
            >
              Regenerate
            </Button>
          )}
        </div>

        <Button
          variant="default"
          size="sm"
          iconName="Download"
          iconPosition="left"
          onClick={onDownloadICS}
          disabled={!contractData?.extractedData?.dates}
        >
          Export Calendar
        </Button>
      </div>

      {/* Info footer */}
      {contractData?.extractedData && (
        <div className="text-xs text-muted-foreground bg-muted/20 rounded p-2">
          <div className="flex items-center space-x-1">
            <Icon name="Info" size={12} color="var(--color-muted-foreground)" />
            <span>
              Email generated from AI analysis • Confidence: {contractData.confidence_score || 0}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailGenerationPanel;
